import { Avatar, Box, Tooltip } from '@mui/material';
import UPSnackBar from 'components/app-container/snackbar';
import ContractDetailPanel from 'components/contract-detail-panel/ContractDetailPanel';
import UPTanStackTable from 'components/tanstack-table/UPTanStackTable';
import { UPTanStackTableColumn } from 'components/tanstack-table/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import { getCostCentersDropdownItemsAction } from 'store/actions/company-cost-centers-actions';
import { costCentersDropdownItemsSelector } from 'store/selectors/company-cost-centers-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  COMPANY_PAYROLL__EMPLOYEE,
  CONTRACTS__ADJUSTED,
  CONTRACTS__CONFIRM_SELECTED,
  CONTRACTS__COST_CENTER,
  CONTRACTS__DATE,
  CONTRACTS__DEVIATION,
  CONTRACTS__EFFECTIVE_TOTAL,
  CONTRACTS__END_BREAK,
  CONTRACTS__END_WORK,
  CONTRACTS__NO_ITEMS_TO_PROCESS,
  CONTRACTS__SAVE_SUCCESS,
  CONTRACTS__SELECTED,
  CONTRACTS__START_BREAK,
  CONTRACTS__START_WORK,
  GENERAL__CLEAR_ALL,
  GENERAL__ERROR_MESSAGE,
  TIME_REGISTRATION__ARCHIVED,
  TIME_REGISTRATION__COMPLETED,
  TIME_REGISTRATION__CONFIRMED,
  TIME_REGISTRATION__FROZEN,
  TIME_REGISTRATION__IN_PROGRESS,
  TIME_REGISTRATION__SUBMITTED,
} from 'translations/constants';
import { ReactComponent as ConfirmIcon } from '../../assets/icons/ConfirmContract.svg';
import AbsenceIndicator from './components/AbsenceIndicator';
import BooleanIndicatorIcon from './components/BooleanIndicatorIcon';
import DeviationCell from './components/DeviationCell';
import EditableTimeCell from './components/EditableTimeCell';
import EmployeeAdjustedIndicator from './components/EmployeeAdjustedIndicator';
import RegistrationStatusBadge from './components/RegistrationStatusBadge';
import ViewDetailsButton from './components/ViewDetailsButton';
import { PAGE_SIZE_OPTIONS } from './constants';
import { useToBeProcessedData } from './hooks/useToBeProcessedData';
import { ToBeProcessedItem } from './types';
import { formatTotalHours } from './utils';

const STATUS_TRANSLATION_MAP: Record<string, string> = {
  InProgress: TIME_REGISTRATION__IN_PROGRESS,
  Completed: TIME_REGISTRATION__COMPLETED,
  Submitted: TIME_REGISTRATION__SUBMITTED,
  Frozen: TIME_REGISTRATION__FROZEN,
  Confirmed: TIME_REGISTRATION__CONFIRMED,
  Archived: TIME_REGISTRATION__ARCHIVED,
};

interface ToBeProcessedViewProps {
  companyId: number;
  week: number;
  year: number;
  date: string | null;
  employeeIds: number[];
  costCenterId: number | null;
  locationId: number | null;
  workerClass: string | null;
  functionId: number | null;
}

const ToBeProcessedView = (props: ToBeProcessedViewProps) => {
  const { companyId, week, year, date, employeeIds, costCenterId, locationId, workerClass, functionId } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const languageId = useAppSelector(profileSelector)?.languageId;
  const costCenters = useAppSelector(costCentersDropdownItemsSelector);

  useEffect(() => {
    if (companyId) {
      dispatch(getCostCentersDropdownItemsAction({ companyId }));
    }
  }, [companyId, dispatch]);

  const {
    items,
    totalItems,
    error,
    clearError,
    pagination,
    setPagination,
    sorting,
    setSorting,
    saveField,
    successMessage,
    clearSuccessMessage,
    errorMessage,
    clearErrorMessage,
    currentPageSelectedRows,
    isAllOnPageSelected,
    isSomeSelected,
    handleSelectAllOnPage,
    handleSelectionChange,
    getSelectionCount,
    handleBulkConfirm,
    bulkConfirmErrors,
  } = useToBeProcessedData(companyId, week, year, date, employeeIds, costCenterId, locationId, workerClass, functionId);

  const [selectedContractDateId, setSelectedContractDateId] = useState<number | null>(null);

  const handleDetailSaveSuccess = useCallback(() => {
    setSelectedContractDateId(null);
    setPagination({ ...pagination });
  }, [pagination, setPagination]);

  const handleSaveField = useCallback(
    (row: ToBeProcessedItem) => (field: string, value: string | number) => {
      saveField(row, field, value);
    },
    [saveField],
  );

  const pageCount = useMemo(() => Math.ceil(totalItems / pagination.pageSize), [totalItems, pagination.pageSize]);

  const footerActions = useMemo(
    () => [
      {
        id: 'confirm',
        label: t(CONTRACTS__CONFIRM_SELECTED),
        icon: <ConfirmIcon />,
        onClick: handleBulkConfirm,
      },
    ],
    [t, handleBulkConfirm],
  );

  // Memoized cell renderers
  const employeeNameCellRenderer = useCallback(
    (info: { row: ToBeProcessedItem }) => (
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar src={info.row.employeeAvatar ?? undefined} sx={{ width: 32, height: 32 }} />
        <span>{`${info.row.employeeFirstName} ${info.row.employeeLastName}`}</span>
      </Box>
    ),
    [],
  );

  const dateCellRenderer = useCallback((info: { row: ToBeProcessedItem }) => {
    const dateObj = new Date(info.row.date);
    return dateObj.toLocaleDateString('en-GB');
  }, []);

  const startWorkCellRenderer = useCallback(
    (info: { row: ToBeProcessedItem }) => {
      if (info.row.noShow) {
        return <AbsenceIndicator reason={info.row.noShowReasonDetail} />;
      }
      return (
        <EditableTimeCell
          value={info.row.startWork}
          field="startWork"
          hoursSource={info.row.hoursSource}
          hasTimeRegistration={info.row.hasTimeRegistration}
          isEmployeeAdjusted={info.row.isEmployeeAdjusted}
          isEditable={info.row.isEditable}
          onSave={handleSaveField(info.row)}
          hasError={!!bulkConfirmErrors[info.row.contractDateId]}
        />
      );
    },
    [handleSaveField, bulkConfirmErrors],
  );

  const endWorkCellRenderer = useCallback(
    (info: { row: ToBeProcessedItem }) => {
      if (info.row.noShow) return null;
      return (
        <EditableTimeCell
          value={info.row.endWork}
          field="endWork"
          hoursSource={info.row.hoursSource}
          hasTimeRegistration={info.row.hasTimeRegistration}
          isEmployeeAdjusted={info.row.isEmployeeAdjusted}
          isEditable={info.row.isEditable}
          onSave={handleSaveField(info.row)}
          hasError={!!bulkConfirmErrors[info.row.contractDateId]}
        />
      );
    },
    [handleSaveField, bulkConfirmErrors],
  );

  const startBreakCellRenderer = useCallback(
    (info: { row: ToBeProcessedItem }) => {
      if (info.row.noShow) return null;
      return (
        <EditableTimeCell
          value={info.row.startBreak ?? ''}
          field="startBreak"
          hoursSource={info.row.hoursSource}
          hasTimeRegistration={info.row.hasTimeRegistration}
          isEmployeeAdjusted={info.row.isEmployeeAdjusted}
          isEditable={info.row.isEditable}
          onSave={handleSaveField(info.row)}
          hasError={!!bulkConfirmErrors[info.row.contractDateId]}
        />
      );
    },
    [handleSaveField, bulkConfirmErrors],
  );

  const endBreakCellRenderer = useCallback(
    (info: { row: ToBeProcessedItem }) => {
      if (info.row.noShow) return null;
      return (
        <EditableTimeCell
          value={info.row.endBreak ?? ''}
          field="endBreak"
          hoursSource={info.row.hoursSource}
          hasTimeRegistration={info.row.hasTimeRegistration}
          isEmployeeAdjusted={info.row.isEmployeeAdjusted}
          isEditable={info.row.isEditable}
          onSave={handleSaveField(info.row)}
          hasError={!!bulkConfirmErrors[info.row.contractDateId]}
        />
      );
    },
    [handleSaveField, bulkConfirmErrors],
  );

  const effectiveTotalCellRenderer = useCallback(
    (info: { row: ToBeProcessedItem }) => {
      if (info.row.noShow) return null;
      return formatTotalHours(info.row.effectiveTotalMinutes, languageId);
    },
    [languageId],
  );

  const deviationCellRenderer = useCallback((info: { row: ToBeProcessedItem }) => {
    if (info.row.noShow) return <BooleanIndicatorIcon value={false} />;
    return <DeviationCell deviationMinutes={info.row.deviationMinutes} />;
  }, []);

  const adjustedCellRenderer = useCallback((info: { row: ToBeProcessedItem }) => {
    if (info.row.noShow) return <BooleanIndicatorIcon value={false} />;
    return <EmployeeAdjustedIndicator isEmployeeAdjusted={info.row.isEmployeeAdjusted} />;
  }, []);

  const costCenterCellRenderer = useCallback(
    (info: { row: ToBeProcessedItem }) => {
      // eslint-disable-next-line eqeqeq
      const costCenter = costCenters.find(cc => cc.value == info.row.costCenterId);
      return <span>{costCenter?.label ?? ''}</span>;
    },
    [costCenters],
  );

  const registrationStatusCellRenderer = useCallback(
    (info: { row: ToBeProcessedItem }) => {
      const translationKey = info.row.timeRegistrationStatus
        ? STATUS_TRANSLATION_MAP[info.row.timeRegistrationStatus]
        : undefined;
      return (
        <Tooltip title={translationKey ? t(translationKey) : ''} disableHoverListener={!translationKey}>
          <span>
            <RegistrationStatusBadge
              hasTimeRegistration={info.row.hasTimeRegistration}
              status={info.row.timeRegistrationStatus}
            />
          </span>
        </Tooltip>
      );
    },
    [t],
  );

  const viewDetailsCellRenderer = useCallback(
    (info: { row: ToBeProcessedItem }) => (
      <ViewDetailsButton isHovered onClick={() => setSelectedContractDateId(info.row.contractDateId)} />
    ),
    [],
  );

  const columns = useMemo<UPTanStackTableColumn<ToBeProcessedItem>[]>(
    () => [
      {
        id: 'employeeName',
        header: t(COMPANY_PAYROLL__EMPLOYEE),
        accessorKey: 'employeeLastName',
        enableSorting: true,
        size: 200,
        cell: employeeNameCellRenderer,
      },
      {
        id: 'date',
        header: t(CONTRACTS__DATE),
        accessorKey: 'date',
        enableSorting: true,
        size: 120,
        cell: dateCellRenderer,
      },
      {
        id: 'startWork',
        header: t(CONTRACTS__START_WORK),
        accessorKey: 'startWork',
        enableSorting: true,
        size: 140,
        cell: startWorkCellRenderer,
        colSpan: (row: ToBeProcessedItem) => (row.noShow ? 4 : 1),
      },
      {
        id: 'endWork',
        header: t(CONTRACTS__END_WORK),
        accessorKey: 'endWork',
        enableSorting: true,
        size: 140,
        cell: endWorkCellRenderer,
      },
      {
        id: 'startBreak',
        header: t(CONTRACTS__START_BREAK),
        accessorKey: 'startBreak',
        enableSorting: true,
        size: 140,
        cell: startBreakCellRenderer,
      },
      {
        id: 'endBreak',
        header: t(CONTRACTS__END_BREAK),
        accessorKey: 'endBreak',
        enableSorting: true,
        size: 140,
        cell: endBreakCellRenderer,
      },
      {
        id: 'effectiveTotalMinutes',
        header: t(CONTRACTS__EFFECTIVE_TOTAL),
        accessorKey: 'effectiveTotalMinutes',
        enableSorting: true,
        size: 120,
        align: 'center',
        headerAlign: 'center',
        cell: effectiveTotalCellRenderer,
      },
      {
        id: 'deviationMinutes',
        header: t(CONTRACTS__DEVIATION),
        accessorKey: 'deviationMinutes',
        enableSorting: true,
        size: 120,
        headerAlign: 'center',
        align: 'center',
        cell: deviationCellRenderer,
      },
      {
        id: 'isEmployeeAdjusted',
        header: t(CONTRACTS__ADJUSTED),
        enableSorting: false,
        size: 80,
        align: 'center',
        headerAlign: 'center',
        cell: adjustedCellRenderer,
      },
      {
        id: 'costCenterId',
        header: t(CONTRACTS__COST_CENTER),
        accessorKey: 'costCenterId',
        enableSorting: true,
        headerAlign: 'center',
        align: 'center',
        size: 160,
        cell: costCenterCellRenderer,
      },
      {
        id: 'registrationStatus',
        header: '',
        enableSorting: false,
        size: 80,
        align: 'center',
        cell: registrationStatusCellRenderer,
      },
      {
        id: 'viewDetails',
        header: '',
        enableSorting: false,
        size: 120,
        cell: viewDetailsCellRenderer,
      },
    ],
    [
      t,
      employeeNameCellRenderer,
      dateCellRenderer,
      startWorkCellRenderer,
      endWorkCellRenderer,
      startBreakCellRenderer,
      endBreakCellRenderer,
      effectiveTotalCellRenderer,
      deviationCellRenderer,
      adjustedCellRenderer,
      costCenterCellRenderer,
      registrationStatusCellRenderer,
      viewDetailsCellRenderer,
    ],
  );

  const selectionCount = getSelectionCount();

  const customHeaderCheckbox = useMemo(
    () => ({
      checked: isAllOnPageSelected,
      indeterminate: isSomeSelected,
      onChange: handleSelectAllOnPage,
    }),
    [isAllOnPageSelected, isSomeSelected, handleSelectAllOnPage],
  );

  const getRowId = useCallback((row: ToBeProcessedItem) => row.contractDateId, []);

  const tableElement = useMemo(
    () => (
      <UPTanStackTable<ToBeProcessedItem>
        data={items}
        columns={columns}
        getRowId={getRowId}
        emptyMessage={t(CONTRACTS__NO_ITEMS_TO_PROCESS)}
        enableRowSelection
        selectedRows={currentPageSelectedRows}
        onSelectionChange={handleSelectionChange}
        customHeaderCheckbox={customHeaderCheckbox}
        footerActions={footerActions}
        footerClearAllText={t(GENERAL__CLEAR_ALL)}
        footerSelectedText={t(CONTRACTS__SELECTED)}
        footerCanClearAll
        footerSelectedCount={selectionCount}
        footerTotalCount={totalItems}
        enableSorting
        manualSorting
        sorting={sorting}
        onSortingChange={setSorting}
        manualPagination
        pagination={pagination}
        onPaginationChange={setPagination}
        pageCount={pageCount}
        totalItems={totalItems}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />
    ),
    [
      items,
      columns,
      getRowId,
      t,
      currentPageSelectedRows,
      handleSelectionChange,
      customHeaderCheckbox,
      footerActions,
      selectionCount,
      totalItems,
      sorting,
      setSorting,
      pagination,
      setPagination,
      pageCount,
    ],
  );

  return (
    <Box>
      <UPSnackBar
        open={!!successMessage}
        onClose={clearSuccessMessage}
        content={t(CONTRACTS__SAVE_SUCCESS)}
        type="success"
        vertical="bottom"
        horizontal="right"
        index={0}
      />
      <UPSnackBar
        open={!!errorMessage}
        onClose={clearErrorMessage}
        content={t(errorMessage)}
        type="error"
        vertical="bottom"
        horizontal="right"
        index={0}
      />
      <UPSnackBar
        open={!!error}
        onClose={clearError}
        content={t(GENERAL__ERROR_MESSAGE)}
        type="error"
        vertical="bottom"
        horizontal="right"
        index={0}
      />
      {tableElement}
      <ContractDetailPanel
        contractDateId={selectedContractDateId}
        companyId={companyId}
        onClose={() => setSelectedContractDateId(null)}
        onSaveSuccess={handleDetailSaveSuccess}
      />
    </Box>
  );
};

export default ToBeProcessedView;
